import i18next from 'i18next';
import PropTypes from 'prop-types';
import React from 'react';

import { InputIncrement } from '@commons/utils/styledLibraryComponents';
import DisplayNumber, { PRICE_NUMBER_DECIMALS } from '@commons/DisplayNumber';
import EmptyState from '@commons/EmptyState';
import Text, { ENUM_COLORS, ENUM_FONTS } from '@commons/Text';

import { Cell, ColumnsRow, ProductRow, TableContainer } from '../../../orderTableStyledComponents';
import { EXTERNAL_ORDER_QUANTITY_LIMITS } from '../../../../constants';
import OrderCategoryRow from '../../../OrderCategoryRow';
import OrderProductNameCell from '../../../OrderProductNameCell';

const QUANTITY_INPUT_WIDTH = '160px';

const COLUMN_FLEX = {
  PRODUCT: 4,
  PRICE: 2,
  QUANTITY: 2,
  TOTAL: 2,
};

/**
 * Catalog of the client as the order table: every line the client can order is already displayed,
 * grouped by category (alphabetical, products alphabetical inside a group), and ordering a product
 * is only a matter of typing its quantity. The summary mode narrows the rows to the ordered ones,
 * keeping the same grouping.
 */
const OrderCatalogTable = (props) => {
  const {
    categoryGroups,
    quantityByProductId,
    collapsedCategories,
    isReadOnly,
    onToggleCategory,
    onChangeQuantity,
  } = props;

  if (!categoryGroups.length) {
    return <EmptyState label={i18next.t('EXTERNAL_ORDERS.FORM.NO_CATALOG_PRODUCT')} />;
  }

  const renderProductRow = ({ productId, name, sku, img, priceWithTaxes }) => {
    const quantity = quantityByProductId[productId] ?? 0;

    return (
      <ProductRow key={productId}>
        <Cell flex={COLUMN_FLEX.PRODUCT}>
          <OrderProductNameCell img={img} name={name} sku={sku} />
        </Cell>
        <Cell flex={COLUMN_FLEX.PRICE}>
          <DisplayNumber
            customNumberDecimal={PRICE_NUMBER_DECIMALS}
            font={ENUM_FONTS.TEXT_BIG_HEIGHT_16}
            number={priceWithTaxes}
            displayCurrencyCode
          />
        </Cell>
        <Cell flex={COLUMN_FLEX.QUANTITY}>
          <InputIncrement
            allowDecimals={false}
            isDisabled={isReadOnly}
            limitValues={EXTERNAL_ORDER_QUANTITY_LIMITS}
            setValue={(value) => onChangeQuantity(productId, value)}
            value={quantity}
            width={QUANTITY_INPUT_WIDTH}
          />
        </Cell>
        <Cell flex={COLUMN_FLEX.TOTAL}>
          <DisplayNumber
            font={ENUM_FONTS.TEXT_BIG_HEIGHT_16}
            number={quantity * priceWithTaxes}
            displayCurrencyCode
          />
        </Cell>
      </ProductRow>
    );
  };

  return (
    <TableContainer>
      <ColumnsRow>
        <Cell flex={COLUMN_FLEX.PRODUCT}>
          <Text color={ENUM_COLORS.DARKER} font={ENUM_FONTS.TEXT_BIG_HEIGHT_16}>
            {i18next.t('GENERAL.PRODUCT')}
          </Text>
        </Cell>
        <Cell flex={COLUMN_FLEX.PRICE}>
          <Text color={ENUM_COLORS.DARKER} font={ENUM_FONTS.TEXT_BIG_HEIGHT_16}>
            {i18next.t('EXTERNAL_ORDERS.FORM.COLUMN_UNIT_PRICE')}
          </Text>
        </Cell>
        <Cell flex={COLUMN_FLEX.QUANTITY}>
          <Text color={ENUM_COLORS.DARKER} font={ENUM_FONTS.TEXT_BIG_HEIGHT_16}>
            {i18next.t('GENERAL.QUANTITY')}
          </Text>
        </Cell>
        <Cell flex={COLUMN_FLEX.TOTAL}>
          <Text color={ENUM_COLORS.DARKER} font={ENUM_FONTS.TEXT_BIG_HEIGHT_16}>
            {i18next.t('EXTERNAL_ORDERS.FORM.COLUMN_TOTAL_WITH_TAXES')}
          </Text>
        </Cell>
      </ColumnsRow>

      {categoryGroups.map(({ category, products }) => {
        const isCollapsed = collapsedCategories.includes(category);

        return (
          <div key={category}>
            <OrderCategoryRow
              category={category}
              isCollapsed={isCollapsed}
              productCount={products.length}
              onToggle={() => onToggleCategory(category)}
            />
            {!isCollapsed && products.map(renderProductRow)}
          </div>
        );
      })}
    </TableContainer>
  );
};

OrderCatalogTable.propTypes = {
  categoryGroups: PropTypes.arrayOf(
    PropTypes.shape({
      category: PropTypes.string,
      products: PropTypes.array,
    }),
  ).isRequired,
  quantityByProductId: PropTypes.objectOf(PropTypes.number).isRequired,
  collapsedCategories: PropTypes.arrayOf(PropTypes.string).isRequired,
  isReadOnly: PropTypes.bool.isRequired,
  onToggleCategory: PropTypes.func.isRequired,
  onChangeQuantity: PropTypes.func.isRequired,
};

export default OrderCatalogTable;
